// ===================================================
// storage.js — 데이터 저장/불러오기 유틸
//
// 이전에는 브라우저 로컬 저장소(localStorage)를 사용했지만,
// 이제 Supabase DB를 사용합니다.
// 덕분에 여러 기기에서도 같은 데이터를 볼 수 있고,
// 계정마다 데이터가 안전하게 분리됩니다.
//
// 저장 형식 (sessions 테이블):
//   id, user_id, date, mode, initial_text, rounds, keywords,
//   title, context_summary, decision_meta, pinned, created_at, updated_at
//
// ※ 의사결정 모드 전용 decision_meta 컬럼 추가 필요:
//   Supabase SQL: ALTER TABLE sessions ADD COLUMN decision_meta jsonb;
//
// ※ 즐겨찾기 기능을 위한 pinned 컬럼 추가 필요:
//   Supabase SQL: ALTER TABLE sessions ADD COLUMN pinned boolean NOT NULL DEFAULT false;
//
// 저장 형식 (chat_messages 테이블):
//   id, user_id, role, text, referenced_count,
//   memory_session_id, is_memory_resurfacing, created_at
//
// 저장 형식 (memory_resurfacing_log 테이블):
//   id, user_id, session_id, session_date, created_at
//
// 저장 형식 (event_logs 테이블):
//   id, user_id, event_type, event_data(jsonb), created_at
//
// 저장 형식 (profiles 테이블):
//   user_id, is_master, created_at
// ===================================================

import { supabase } from './supabase';
import { getToday } from './utils/dateUtils';
import { logError } from './utils/errorLogger';

// 현재 로그인한 사용자 ID를 가져옴
// getSession()으로 로컬 세션에서 user id를 가져옴 (서버 HTTP 호출 없음)
// getUser()는 서버 검증이라 토큰 만료 시 실패 → 빈 데이터 반환 원인이었음
async function getUserId() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id || null;
}

// ── 세션 기록 ──────────────────────────────────────────────────

// 저장된 전체 기록 불러오기
// 반환 형식: { "YYYY-MM-DD": [{ id, mode, initialText, rounds, ... }, ...], ... }
export async function loadSessions() {
  const userId = await getUserId();
  if (!userId) return {};

  const { data, error } = await supabase
    .from('sessions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (error || !data) {
    if (error) {
      logError({
        action: 'loadSessions',
        error,
        screen: 'app',
        context: { userId },
      });
    }
    return {};
  }

  // DB의 평면(flat) 행 목록을 날짜별로 묶인 중첩 형식으로 변환
  const result = {};
  for (const row of data) {
    if (!result[row.date]) result[row.date] = [];
    result[row.date].push({
      id: row.id,                          // DB 고유 ID (수정/삭제 시 사용)
      mode: row.mode,
      initialText: row.initial_text,
      rounds: row.rounds || [],
      keywords: row.keywords || [],
      title: row.title,
      contextSummary: row.context_summary,
      // 의사결정 모드 전용 메타데이터 (decision_meta 컬럼이 없으면 null)
      decisionMeta: row.decision_meta || null,
      // 꿈과 목표 모드 전용 메타데이터 (vision_meta 컬럼)
      visionMeta: row.vision_meta || null,
      // 즐겨찾기 여부 (pinned 컬럼이 없는 구버전 데이터는 false로 처리)
      pinned: row.pinned ?? false,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    });
  }
  return result;
}

// 새 세션을 오늘 날짜로 저장
// 반환: 삽입된 행의 id (string) — 임베딩 저장 등 후속 작업에 사용
export async function saveSession(session) {
  const userId = await getUserId();
  if (!userId) return null;

  const { data, error } = await supabase.from('sessions').insert({
    user_id: userId,
    date: getToday(),
    mode: session.mode,
    initial_text: session.initialText,
    rounds: session.rounds,
    keywords: session.keywords,
    title: session.title,
    context_summary: session.contextSummary,
    // 의사결정 모드일 때만 저장 (다른 모드는 null)
    decision_meta: session.decisionMeta || null,
  }).select('id').single();

  if (error || !data) return null;
  return data.id;
}

// C2: 세션 임베딩 저장
// saveSession 후 백그라운드에서 호출됩니다.
// sessionId: saveSession이 반환한 DB 행 id
// embedding: generateEmbedding()이 반환한 1536차원 float 배열
export async function saveSessionEmbedding(sessionId, embedding) {
  if (!sessionId || !embedding) return;
  await supabase
    .from('sessions')
    .update({ embedding })
    .eq('id', sessionId);
}

// 특정 세션을 ID로 찾아 일부 필드를 업데이트
// sessionId: Supabase DB의 uuid (loadSessions 결과의 각 세션 객체에 포함)
// updatedFields: 덮어쓸 필드만 전달
export async function updateSession(sessionId, updatedFields) {
  const dbFields = { updated_at: new Date().toISOString() };

  if (updatedFields.initialText !== undefined) dbFields.initial_text = updatedFields.initialText;
  if (updatedFields.rounds !== undefined)      dbFields.rounds = updatedFields.rounds;
  if (updatedFields.keywords !== undefined)    dbFields.keywords = updatedFields.keywords;
  if (updatedFields.contextSummary !== undefined) dbFields.context_summary = updatedFields.contextSummary;
  if (updatedFields.title !== undefined)       dbFields.title = updatedFields.title;
  if (updatedFields.decisionMeta !== undefined) dbFields.decision_meta = updatedFields.decisionMeta;
  if (updatedFields.pinned !== undefined) dbFields.pinned = updatedFields.pinned;

  await supabase.from('sessions').update(dbFields).eq('id', sessionId);
}

// 특정 세션을 ID로 삭제
export async function deleteSession(sessionId) {
  await supabase.from('sessions').delete().eq('id', sessionId);
}

// 현재 사용자의 모든 기록 삭제 (설정 화면의 '전체 삭제')
export async function deleteAllSessions() {
  const userId = await getUserId();
  if (!userId) return;
  await supabase.from('sessions').delete().eq('user_id', userId);
}

// ── 채팅 내역 ──────────────────────────────────────────────────

// 저장된 채팅 메시지 목록 불러오기 (최대 200개, 오래된 순)
// 반환 형식: [{ role, text, referencedCount?, memorySessionId?, isMemoryResurfacing?, createdAt }]
export async function loadChatMessages() {
  const userId = await getUserId();
  if (!userId) return [];

  const { data } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true })
    .limit(200);

  return (data || []).map(row => ({
    role: row.role,
    text: row.text,
    ...(row.referenced_count != null && { referencedCount: row.referenced_count }),
    ...(row.memory_session_id != null && { memorySessionId: row.memory_session_id }),
    isMemoryResurfacing: row.is_memory_resurfacing ?? false,
    createdAt: row.created_at,
  }));
}

// 채팅 메시지 1개를 DB에 추가
// msg: {
//   role: 'user' | 'assistant',
//   text: string,
//   referencedCount?: number,
//   memorySessionId?: string,    // 기억 되살리기: 원본 세션 ID
//   isMemoryResurfacing?: boolean // 기억 되살리기 메시지 여부
// }
export async function appendChatMessage(msg) {
  const userId = await getUserId();
  if (!userId) return;

  await supabase.from('chat_messages').insert({
    user_id: userId,
    role: msg.role,
    text: msg.text,
    referenced_count: msg.referencedCount ?? null,
    memory_session_id: msg.memorySessionId ?? null,
    is_memory_resurfacing: msg.isMemoryResurfacing ?? false,
  });
}

// ── 기억 되살리기 로그 ──────────────────────────────────────────

// 오늘 하루 동안 기억 되살리기가 몇 번 실행됐는지 확인
// (하루 최대 2회 제한을 위해 사용)
export async function getTodayResurfacingCount(userId) {
  const today = getToday();
  const { data } = await supabase
    .from('memory_resurfacing_log')
    .select('id')
    .eq('user_id', userId)
    .gte('created_at', `${today}T00:00:00.000Z`);

  return (data || []).length;
}

// 최근 N일 이내에 기억 되살리기 대상이 됐던 세션 ID 목록 조회
// (같은 날 기록이 7일 이내에 재선정되는 것을 방지하기 위해 사용)
export async function getRecentResurfacingSessionIds(userId, days) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  const { data } = await supabase
    .from('memory_resurfacing_log')
    .select('session_id')
    .eq('user_id', userId)
    .gte('created_at', cutoff.toISOString());

  return new Set((data || []).map(row => row.session_id));
}

// 기억 되살리기 실행 기록을 로그 테이블에 저장
export async function saveResurfacingLog(userId, sessionId, sessionDate) {
  await supabase.from('memory_resurfacing_log').insert({
    user_id: userId,
    session_id: sessionId,
    session_date: sessionDate,
  });
}

// 가장 최근에 AI가 보낸 메시지의 생성 시각을 반환
// (기억 되살리기, 주간 회고, 연간 기록 등 모든 AI 자동 메시지 포함)
// 읽지 않은 채팅 여부 판단에 사용
export async function getLastAIMessageTime(userId) {
  const { data } = await supabase
    .from('chat_messages')
    .select('created_at')
    .eq('user_id', userId)
    .eq('role', 'assistant')
    .order('created_at', { ascending: false })
    .limit(1);

  return data?.[0]?.created_at ?? null;
}

// ── 질문 선호도 피드백 ──────────────────────────────────────────

// 질문에 대한 유저 피드백 1건 저장
// action: 'accepted' | 'rejected' | 'deep_answer' | 'resonant_axis' | 'reported'
// reported: 유저가 질문 신고 버튼을 누른 경우 (QA용)
export async function saveQuestionFeedback({ mode, questionText, action, strategy = null, reportReason = null, initialText = null, round = null, priorRounds = null }) {
  const userId = await getUserId();
  if (!userId) return;

  const row = {
    user_id: userId,
    mode,
    question_text: questionText,
    action,
  };
  // strategy 컬럼: 질문 생성 시 사용된 전략 태그 (factual, hypothetical 등)
  if (strategy) row.strategy = strategy;
  // 신고(reported) 전용 필드
  if (reportReason) row.report_reason = reportReason;
  if (initialText) row.initial_text = initialText;
  if (round != null) row.round = round;
  if (priorRounds && priorRounds.length > 0) row.prior_rounds = priorRounds;

  const { error } = await supabase.from('question_feedback').insert(row);
  if (error) throw error;
}

// 특정 모드에서 쌓인 피드백 총 개수 조회
export async function getQuestionFeedbackCount(mode) {
  const userId = await getUserId();
  if (!userId) return 0;

  const { count } = await supabase
    .from('question_feedback')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('mode', mode);

  return count ?? 0;
}

// 선호도 요약 생성에 쓸 최근 피드백 목록 조회
// 반환 형식: [{ question_text, action }, ...]
export async function getRecentQuestionFeedback(mode, limit = 30) {
  const userId = await getUserId();
  if (!userId) return [];

  const { data } = await supabase
    .from('question_feedback')
    .select('question_text, action')
    .eq('user_id', userId)
    .eq('mode', mode)
    .order('created_at', { ascending: false })
    .limit(limit);

  return data || [];
}

// ── 선호도 요약 캐시 ──────────────────────────────────────────────

// 저장된 선호도 요약 조회 (없으면 null)
// 반환 형식: { summary, feedbackCount } | null
export async function getPreferenceSummary(mode) {
  const userId = await getUserId();
  if (!userId) return null;

  const { data } = await supabase
    .from('preference_summary')
    .select('summary, feedback_count')
    .eq('user_id', userId)
    .eq('mode', mode)
    .single();

  return data ? { summary: data.summary, feedbackCount: data.feedback_count } : null;
}

// 선호도 요약 저장 또는 갱신 (user_id + mode 조합으로 upsert)
export async function upsertPreferenceSummary({ mode, summary, feedbackCount }) {
  const userId = await getUserId();
  if (!userId) return;

  await supabase.from('preference_summary').upsert({
    user_id: userId,
    mode,
    summary,
    feedback_count: feedbackCount,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id,mode' });
}

// ── 게스트 마이그레이션 ─────────────────────────────────────────

// 게스트 로컬스토리지 세션을 DB로 이전
// 원래 날짜(session.date)를 보존해서 저장함
// 로그인 직후 호출 — 게스트 기록 → 계정 기록으로 옮길 때 사용
export async function migrateGuestSession(session) {
  const userId = await getUserId();
  if (!userId) return;

  await supabase.from('sessions').insert({
    user_id: userId,
    date: session.date,               // 원래 작성 날짜 유지
    mode: session.mode,
    initial_text: session.initialText,
    rounds: session.rounds,
    keywords: session.keywords,
    title: session.title,
    context_summary: session.contextSummary,
    decision_meta: session.decisionMeta || null,
  });
}

// 현재 사용자의 채팅 내역 전체 삭제
export async function clearChatMessages() {
  const userId = await getUserId();
  if (!userId) return;
  await supabase.from('chat_messages').delete().eq('user_id', userId);
}

// ── 이벤트 로그 기록 ───────────────────────────────────────────
// 유저 행동 이벤트를 event_logs 테이블에 저장합니다.
// 가설 검증(H0 세션 완료율 등)에 사용됩니다.
// 실패해도 앱 동작에 영향을 주지 않도록 에러를 무시합니다.
//
// eventType: 'session_start' | 'session_complete' | 'app_open'
// eventData: 이벤트에 대한 추가 정보 (모드, 기록 ID 등)
export async function logEvent(userId, eventType, eventData = {}) {
  if (!userId) return;
  try {
    await supabase.from('event_logs').insert({
      user_id: userId,
      event_type: eventType,
      event_data: eventData,
    });
  } catch {
    // 이벤트 로그 실패는 앱 동작에 영향 없음 — 무시
  }
}

// ── 유저 삶 흐름 요약 (B1) ────────────────────────────────────────
// profiles 테이블의 life_summary 컬럼을 읽고 씁니다.
// 기록을 저장할 때마다 백그라운드에서 갱신됩니다.

// 저장된 삶 흐름 요약 조회 (없으면 null 반환)
export async function fetchLifeSummary() {
  const userId = await getUserId();
  if (!userId) return null;
  try {
    const { data } = await supabase
      .from('profiles')
      .select('life_summary')
      .eq('user_id', userId)
      .single();
    return data?.life_summary || null;
  } catch {
    return null;
  }
}

// 삶 흐름 요약 저장 또는 갱신 (user_id 기준 upsert)
export async function upsertLifeSummary(summary) {
  const userId = await getUserId();
  if (!userId) return;
  await supabase
    .from('profiles')
    .upsert({ user_id: userId, life_summary: summary }, { onConflict: 'user_id' });
}

// ── 마스터 계정 여부 확인 ──────────────────────────────────────
// profiles 테이블에서 is_master 값을 조회합니다.
// 로그인 직후 1회 호출합니다.
export async function checkIsMaster(userId) {
  if (!userId) return false;
  try {
    const { data } = await supabase
      .from('profiles')
      .select('is_master')
      .eq('user_id', userId)
      .single();
    return data?.is_master === true;
  } catch {
    return false;
  }
}

// ── 계정 완전 삭제 ──────────────────────────────────────────────
// 서버(Edge Function)에 계정 삭제 요청을 보냅니다.
// Edge Function이 모든 데이터를 삭제한 뒤 인증 계정까지 제거합니다.
// 앱스토어/플레이스토어 규정: 유저가 앱 내에서 계정 삭제를 할 수 있어야 합니다.
export async function deleteAccount() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('로그인 상태가 아닙니다.');

  const res = await fetch('/api/delete-account', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || '계정 삭제에 실패했어요.');
  }
}
